import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  User, ShieldCheck, MapPin, Mail, Hash, Building2, Users, KeyRound, LogOut, Loader2, Check, AlertTriangle, Pencil, Save,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

// Whitelisted values for the "Supervisor type" field. Deliberately excludes
// any role-tier keywords (superintendent, owner, admin, control, planner) so
// a supervisor CANNOT bump themselves up via the profile screen — Josh's
// requirement. Role escalation is Owner-Center-only.
const SUPERVISOR_TYPE_OPTIONS = [
  "Maintenance",
  "Production",
  "Fabrication",
  "Electrical",
  "Wash Bay",
  "Auto-Elec",
];

// Trades roster on site. Josh (Feb 27): "Replace 'Mechanic' with 'Fitter'"
// and add "HV Sparky" as a distinct high-voltage electrician role. Keep
// the list tight so the dropdown scans fast on tablet.
const TRADE_OPTIONS = [
  "Fitter",
  "Boilermaker",
  "Auto-Elec",
  "HV Sparky",
  "Electrician",
  "Cleaner",
  "Operator",
  "Tradesman",
  "Supervisor",
  "Planner",
  "Other",
];

/**
 * Profile — the "me" screen. Shows account info pulled from /auth/me and lets
 * the user edit their personal details (name, trade, email, work area,
 * supervisor type) plus rotate their 4-digit PIN. Site + crew assignments
 * still come from Owner Center — everything else is self-service here.
 */
export default function Profile() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ inspections: null, breakdowns: null, ppfs: null });
  // Editable profile draft — seeded from the auth context on mount and
  // whenever the user record changes. Save button flushes via PATCH /auth/me.
  const [draft, setDraft] = useState(() => shapeFromUser(user));
  const [editing, setEditing] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);

  useEffect(() => {
    setDraft(shapeFromUser(user));
  }, [user]);

  const dirty = useMemo(() => {
    if (!user) return false;
    const cur = shapeFromUser(user);
    return Object.keys(draft).some((k) => (draft[k] || "") !== (cur[k] || ""));
  }, [draft, user]);

  const saveProfile = async (e) => {
    e?.preventDefault?.();
    if (!dirty || savingProfile) return;
    setSavingProfile(true); setProfileMsg(null);
    try {
      // PATCH /auth/me — schema: { name, trade, email, crew_id, work_area, supervisor_type }.
      // WHITELISTED fields only. `role`, `is_admin`, `permissions`, `site_code`,
      // `crew_id`, and anything else that could escalate privileges are DELIBERATELY
      // excluded — profile edits must never let a supervisor jump to
      // superintendent/owner. Role tier is Owner-Center only.
      const ALLOWED = ["name", "trade", "email", "work_area", "supervisor_type"];
      // Additional guard on supervisor_type — must match the whitelist above
      // so the client-side dropdown can't be bypassed by injecting a payload.
      if (draft.supervisor_type && !SUPERVISOR_TYPE_OPTIONS.includes(draft.supervisor_type)) {
        setProfileMsg({ type: "err", text: "Invalid supervisor type — pick from the list." });
        setSavingProfile(false);
        return;
      }
      const body = {};
      ALLOWED.forEach((k) => {
        const cur = (user[k] || "");
        if ((draft[k] || "") !== cur) body[k] = (draft[k] || "").trim() || undefined;
      });
      await api.patch("/auth/me", body);
      setProfileMsg({ type: "ok", text: "Profile updated." });
      setEditing(false);
      refresh?.();
    } catch (ex) {
      setProfileMsg({ type: "err", text: apiError(ex) });
    } finally {
      setSavingProfile(false);
    }
  };

  const [pin, setPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    (async () => {
      const [ins, bd, pp] = await Promise.allSettled([
        api.get("/inspections?mine=1").catch(() => api.get("/inspections")),
        api.get("/breakdowns?mine=1").catch(() => api.get("/breakdowns")),
        api.get("/ppfs?mine=1").catch(() => api.get("/ppfs")),
      ]);
      const cnt = (r) => {
        if (r.status !== "fulfilled") return null;
        const d = r.value.data;
        const arr = Array.isArray(d) ? d : d?.items || d?.results || d?.data || [];
        // Filter to items authored by the current user when we can.
        const mine = arr.filter((it) => {
          const who = String(
            it.created_by || it.reported_by || it.inspector_id || it.assignee_id || it.user_id || ""
          );
          return !user?.id || !who || who === String(user.id);
        });
        return mine.length;
      };
      setStats({ inspections: cnt(ins), breakdowns: cnt(bd), ppfs: cnt(pp) });
    })();
  }, [user?.id]);

  const changePin = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (!/^\d{4}$/.test(newPin)) { setMsg({ type: "err", text: "New PIN must be 4 digits." }); return; }
    if (newPin !== confirmPin) { setMsg({ type: "err", text: "New PINs don't match." }); return; }
    setBusy(true);
    try {
      // Backend endpoint (verified via OpenAPI): PATCH /api/auth/me/pin.
      // Legacy /auth/change-pin kept as a fallback for any older backend.
      const body = { current_pin: pin, new_pin: newPin };
      try {
        await api.patch("/auth/me/pin", body);
      } catch (ex) {
        if (ex?.response?.status === 404) {
          await api.post("/auth/change-pin", body);
        } else {
          throw ex;
        }
      }
      setMsg({ type: "ok", text: "PIN updated." });
      setPin(""); setNewPin(""); setConfirmPin("");
      refresh?.();
    } catch (ex) {
      setMsg({ type: "err", text: apiError(ex) });
    } finally {
      setBusy(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center gap-3 text-muted">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading profile…
      </div>
    );
  }

  const role = user.role || "user";
  const initials = String(user.name || user.full_name || user.username || "?")
    .split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="space-y-6" data-testid="profile-page">
      {/* Header */}
      <section className="panel-heavy p-6 md:p-8 flex flex-col md:flex-row gap-6 items-start md:items-center">
        <div
          className="w-24 h-24 rounded-2xl flex items-center justify-center font-display font-black text-4xl shrink-0"
          style={{ background: "var(--primary)", color: "#0a0a0a" }}
          data-testid="profile-avatar"
        >
          {initials || <User className="w-10 h-10" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.3em] font-black text-muted">Your Profile</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight leading-none mt-1" data-testid="profile-name">
            {user.name || user.full_name || user.username}
          </h1>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <span className="pill pill-orange" data-testid="profile-role"><ShieldCheck className="w-3.5 h-3.5" /> {role}</span>
            {user.site_code && (
              <span className="pill"><MapPin className="w-3.5 h-3.5" /> {user.site_code}</span>
            )}
            {user.crew_name && (
              <span className="pill"><Users className="w-3.5 h-3.5" /> {user.crew_name}</span>
            )}
            {user.email && (
              <span className="pill"><Mail className="w-3.5 h-3.5" /> {user.email}</span>
            )}
          </div>
        </div>
        <button className="btn btn-ghost" onClick={async () => { await logout(); navigate("/login"); }} data-testid="profile-logout">
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </section>

      {/* Details — editable personal info. Read-only when not in edit mode
          so nothing changes by mistake; hit "Edit" to switch to inputs. */}
      <section className="panel-heavy p-5 md:p-6" data-testid="profile-details-panel">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted">Personal details</div>
            <div className="font-display font-black text-xl tracking-tight">Your info · edit any field</div>
          </div>
          {!editing ? (
            <button
              className="btn btn-sm btn-primary"
              onClick={() => { setEditing(true); setProfileMsg(null); }}
              data-testid="profile-edit"
            >
              <Pencil className="w-4 h-4" /> Edit
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                className="btn btn-sm btn-ghost"
                onClick={() => { setDraft(shapeFromUser(user)); setEditing(false); setProfileMsg(null); }}
                data-testid="profile-cancel"
              >
                Cancel
              </button>
              <button
                className="btn btn-sm btn-primary"
                onClick={saveProfile}
                disabled={!dirty || savingProfile}
                data-testid="profile-save"
              >
                {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="profile-details-grid">
          <EditRow
            icon={User}
            label="Full name"
            value={draft.name}
            onChange={(v) => setDraft({ ...draft, name: v })}
            editing={editing}
            placeholder="e.g. Josh Argall"
            testid="profile-input-name"
          />
          <EditRow
            icon={Mail}
            label="Email"
            value={draft.email}
            onChange={(v) => setDraft({ ...draft, email: v })}
            editing={editing}
            type="email"
            placeholder="you@company.com — used as your default email recipient"
            testid="profile-input-email"
          />
          <EditRow
            icon={ShieldCheck}
            label="Trade"
            value={draft.trade}
            onChange={(v) => setDraft({ ...draft, trade: v })}
            editing={editing}
            options={TRADE_OPTIONS}
            testid="profile-input-trade"
          />
          <EditRow
            icon={MapPin}
            label="Work area"
            value={draft.work_area}
            onChange={(v) => setDraft({ ...draft, work_area: v })}
            editing={editing}
            placeholder="e.g. Field, Workshop, CHPP"
            testid="profile-input-work-area"
          />
          {(user.role === "supervisor" || user.role === "superintendent" || user.role === "owner" || user.is_admin) && (
            <EditRow
              icon={ShieldCheck}
              label="Supervisor type"
              value={draft.supervisor_type}
              onChange={(v) => setDraft({ ...draft, supervisor_type: v })}
              editing={editing}
              placeholder="Discipline you supervise (e.g. Maintenance)"
              testid="profile-input-supervisor-type"
              // Locked whitelist — this field is your DISCIPLINE (Maintenance /
              // Production / etc.), not your ROLE TIER. Escalating from
              // Supervisor to Superintendent has to come from the Owner
              // Center; there's no free-text here to prevent Josh's concern
              // of "supervisor bumping themselves up" via the profile screen.
              options={SUPERVISOR_TYPE_OPTIONS}
            />
          )}
          {/* Role — read-only. Owner Center is the only place this changes. */}
          <ReadOnlyRow icon={ShieldCheck} label="Role" value={user.role || "worker"} testid="profile-role" hint="Only Owner Center can change this" />
          {/* Read-only rows — server-managed, cannot be self-edited. */}
          <ReadOnlyRow icon={User} label="Username" value={user.username || user.login} testid="profile-username" />
          <ReadOnlyRow icon={Building2} label="Default Site" value={user.site_code || "—"} testid="profile-site" />
          <ReadOnlyRow icon={Users} label="Crew" value={user.crew_name || user.crew_id || "—"} testid="profile-crew" hint="Owner Center changes this" />
          <ReadOnlyRow icon={Hash} label="User ID" value={user.id || user._id} mono testid="profile-id" />
        </div>

        {profileMsg && (
          <div
            className={`pill ${profileMsg.type === "ok" ? "pill-green" : "pill-red"} !text-xs !py-2 !px-3 justify-start mt-4`}
            data-testid="profile-msg"
          >
            {profileMsg.type === "ok" ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
            {profileMsg.text}
          </div>
        )}
      </section>

      {/* My activity */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <ActivityTile label="Inspections logged" value={stats.inspections} testid="profile-stat-inspections" />
        <ActivityTile label="Breakdowns raised" value={stats.breakdowns} testid="profile-stat-breakdowns" />
        <ActivityTile label="Planning Requests owned" value={stats.ppfs} testid="profile-stat-ppfs" />
      </section>

      {/* PIN change */}
      <section className="panel-heavy p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
               style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
            <KeyRound className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted">Security</div>
            <div className="font-display font-black text-xl tracking-tight">Change your 4-digit PIN</div>
          </div>
        </div>
        <form className="grid grid-cols-1 md:grid-cols-3 gap-3" onSubmit={changePin}>
          <PinInput label="Current PIN" value={pin} onChange={setPin} testid="pin-current" />
          <PinInput label="New PIN" value={newPin} onChange={setNewPin} testid="pin-new" />
          <PinInput label="Confirm new PIN" value={confirmPin} onChange={setConfirmPin} testid="pin-confirm" />
          <div className="md:col-span-3 flex items-center gap-3">
            <button
              className="btn btn-primary"
              type="submit"
              disabled={busy || !pin || !newPin || !confirmPin}
              data-testid="pin-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
              Update PIN
            </button>
            {msg && (
              <span
                className={`pill ${msg.type === "ok" ? "pill-green" : "pill-red"} !text-xs !py-1.5 !px-3 justify-start`}
                data-testid="pin-msg"
              >
                {msg.type === "ok" ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />} {msg.text}
              </span>
            )}
          </div>
        </form>
      </section>
    </div>
  );
}

function shapeFromUser(user) {
  if (!user) return { name: "", trade: "", email: "", work_area: "", supervisor_type: "" };
  return {
    name: user.name || user.full_name || "",
    trade: user.trade || "",
    email: user.email || "",
    work_area: user.work_area || "",
    supervisor_type: user.supervisor_type || "",
  };
}

function EditRow({ icon: Icon, label, value, onChange, editing, placeholder, type = "text", testid, options }) {
  return (
    <div className="panel p-4" data-testid={testid ? `${testid}-cell` : undefined}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted mb-1.5">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      {editing ? (
        options ? (
          // Locked-value field — user can only pick from the safe whitelist,
          // never type a role-escalating keyword like "superintendent".
          <select
            className="input"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testid}
          >
            <option value="">— Select —</option>
            {options.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        ) : (
          <input
            className="input"
            type={type}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder || label}
            data-testid={testid}
          />
        )
      ) : (
        <div className="font-display font-black text-lg truncate min-h-[28px] flex items-center">
          {value || <span className="text-muted font-normal text-sm">— not set — click Edit to add</span>}
        </div>
      )}
    </div>
  );
}

function ReadOnlyRow({ icon: Icon, label, value, mono, hint, testid }) {
  return (
    <div className="panel p-4" data-testid={testid} style={{ opacity: 0.85 }}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted mb-1.5">
        <Icon className="w-3.5 h-3.5" /> {label}
        {hint && <span className="text-[9px] normal-case tracking-normal opacity-70">· {hint}</span>}
      </div>
      <div className={`font-display font-black text-lg truncate ${mono ? "font-mono-tight" : ""}`}>
        {value || "—"}
      </div>
    </div>
  );
}

function ActivityTile({ label, value, testid }) {
  return (
    <div className="panel p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-muted">{label}</div>
      <div className="font-display font-black text-4xl mt-1 tabular-nums" style={{ color: "var(--primary)" }}>
        {value == null ? "—" : value}
      </div>
    </div>
  );
}

function PinInput({ label, value, onChange, testid }) {
  return (
    <div className="field">
      <label>{label}</label>
      <input
        className="input"
        type="password"
        inputMode="numeric"
        maxLength={4}
        pattern="[0-9]{4}"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 4))}
        placeholder="••••"
        data-testid={testid}
      />
    </div>
  );
}
